import React, { useRef, useCallback, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./quill-custom.css";
import { Box } from "@mui/material";
import CustomFormLabel from "../forms/theme-elements/CustomFormLabel";
import { PostFunction } from "src/utils/axios_connection";
import UrlApi from "src/utils/Url";
import { ChackImageIsFile } from "src/helper/publicFunction";
// import { uploadImage } from "../../services/api"; // You'll need to create this service

// Quill draws its toolbar as unnamed buttons and pickers. Name each one
// (screen readers and voice control read "Bold", "Bulleted list", ...).
const BUTTON_NAMES = {
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  strike: "Strikethrough",
  blockquote: "Quote",
  "code-block": "Code block",
  "script:sub": "Subscript",
  "script:super": "Superscript",
  "list:ordered": "Numbered list",
  "list:bullet": "Bulleted list",
  "list:check": "Checklist",
  "indent:-1": "Decrease indent",
  "indent:+1": "Increase indent",
  "direction:rtl": "Right-to-left text",
  "header:1": "Heading 1",
  "header:2": "Heading 2",
  link: "Link",
  image: "Image",
  video: "Video",
  clean: "Clear formatting",
};
const PICKER_NAMES = { size: "Text size", header: "Heading", color: "Text colour", background: "Highlight colour", align: "Alignment", font: "Font" };
const PICKER_ITEM = {
  size: (v) => (v ? `Size ${v}` : "Normal size"),
  header: (v) => (v ? `Heading ${v}` : "Normal text"),
  color: (v) => (v ? `Colour ${v}` : "Default colour"),
  background: (v) => (v ? `Highlight ${v}` : "No highlight"),
  align: (v) => (v ? `Align ${v}` : "Align left"),
  font: (v) => (v ? `Font ${v}` : "Default font"),
};

export function nameToolbar(toolbar) {
  if (!toolbar) return;
  toolbar.querySelectorAll("button").forEach((button) => {
    const format = [...button.classList].find((c) => c.startsWith("ql-"))?.slice(3);
    if (!format) return;
    const value = button.getAttribute("value");
    const name = BUTTON_NAMES[value ? `${format}:${value}` : format] || BUTTON_NAMES[format];
    if (name) button.setAttribute("aria-label", name);
  });
  toolbar.querySelectorAll(".ql-picker").forEach((picker) => {
    const format = [...picker.classList].find((c) => c.startsWith("ql-") && c !== "ql-picker" && !c.endsWith("-picker"))?.slice(3);
    const label = picker.querySelector(".ql-picker-label");
    if (label && PICKER_NAMES[format]) label.setAttribute("aria-label", PICKER_NAMES[format]);
    picker.querySelectorAll(".ql-picker-item").forEach((item) => {
      const name = PICKER_ITEM[format]?.(item.getAttribute("data-value"));
      if (name) item.setAttribute("aria-label", name);
    });
  });
}

// height: the text area's height in px (it scrolls past it). Without it the
// area starts at 200px and grows with the text up to 520px.
const EditorComponent = ({ data, title, onChange, disabled = false, height }) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    nameToolbar(editor?.getModule("toolbar")?.container);
  }, [disabled]);

  // Quill re-writes the HTML it is given when it loads it ("api" changes):
  // passing that on made forms look edited before anyone typed.
  const handleEditorChange = (content, delta, source) => {
    if (source === "api") return;
    onChange(content);
  };

  const UploadFileMessage = async ({ callback, file }) => {
    try {
      var bodyFormData = new FormData();
      if (
        file &&
        file != null &&
        file != undefined &&
        ChackImageIsFile({ data: file, IsBoolean: true })
      ) {
        bodyFormData.append("file", file);
      } else if (!file) {
        bodyFormData.append("file", "");
      }
      const response = await PostFunction({
        data: bodyFormData,
        Route: UrlApi.chat.upload,
        showalert: false,
        state: null,
        isMultipart: true,
        dispatch: null,
        is_client: true,
      });
      callback(response);
    } catch (err) {
      callback(false);
    }
  };

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        // Here you would typically upload the file to your server
        // and get back the image URL
        const formData = new FormData();
        formData.append("image", file);

        // Replace with your actual upload endpoint
        await UploadFileMessage({
          callback: (data) => {
            // Insert the image into the editor
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true);
            quill.editor.insertEmbed(
              range.index,
              "image",
              UrlApi.baseUrlImage + data?.data
            );
            quill.setSelection(range.index + 1);
            handleEditorChange(quill.root.innerHTML);
          },
          file,
        });
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    };
  }, []);

  const modules = {
    toolbar: {
      container: [
        [{ size: [] }],
        [
          { header: "1" },
          { header: "2" },
          { header: [3, 4, 5, 6] },
          { color: [] },
          { background: [] },
        ],
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ script: "sub" }, { script: "super" }],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        [{ direction: "rtl" }],
        [{ align: [] }],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: {
        image: imageHandler,
      },
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "color",
    "background",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "script",
    "list",
    "bullet",
    "indent",
    "direction",
    "align",
    "link",
  ];

  // The toolbar and text area follow the theme (Quill's own colours are
  // black on white, invisible in dark mode). Read-only hides the toolbar.
  return (
    <Box
      sx={(theme) => {
        const t = theme.palette.safrati || {};
        const ink2 = theme.palette.text.secondary;
        return {
          mb: 2.5,
          "& .ql-toolbar.ql-snow": {
            display: disabled ? "none" : undefined,
            border: `1px solid ${t.line2}`,
            borderRadius: "14px 14px 0 0",
            bgcolor: "background.paper",
            fontFamily: theme.typography.fontFamily,
          },
          "& .ql-container.ql-snow": {
            border: `1px solid ${t.line2}`,
            borderTop: disabled ? undefined : 0,
            borderRadius: disabled ? "14px" : "0 0 14px 14px",
            bgcolor: "background.paper",
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.9375rem",
          },
          "&:focus-within .ql-container.ql-snow, &:focus-within .ql-toolbar.ql-snow": { borderColor: theme.palette.primary.main },
          "& .ql-editor": {
            ...(height ? { height } : { minHeight: 200, maxHeight: 520 }),
            overflowY: "auto",
            color: "text.primary",
            lineHeight: 1.6,
          },
          "& .ql-editor.ql-blank::before": { color: theme.palette.text.disabled, fontStyle: "normal" },
          "& .ql-snow .ql-stroke": { stroke: ink2 },
          "& .ql-snow .ql-fill": { fill: ink2 },
          "& .ql-snow .ql-picker": { color: ink2 },
          "& .ql-snow .ql-picker-options": { bgcolor: "background.paper", borderColor: t.line2 },
          "& .ql-snow button:hover .ql-stroke, & .ql-snow button.ql-active .ql-stroke": { stroke: theme.palette.primary.main },
          "& .ql-snow button:hover .ql-fill, & .ql-snow button.ql-active .ql-fill": { fill: theme.palette.primary.main },
          "& .ql-snow button:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, borderRadius: 4 },
          "& .ql-snow a": { color: theme.palette.primary.main },
        };
      }}
    >
      {title ? <CustomFormLabel>{title}</CustomFormLabel> : null}
      <ReactQuill
        ref={quillRef}
        value={data}
        onChange={handleEditorChange}
        readOnly={disabled}
        modules={modules}
        formats={[...formats, "image", "video"]}
      />
    </Box>
  );
};

export default EditorComponent;
